/* WelcomeCompleteSheet — mandatory "finish your profile" modal for users
   who signed up via Google (or any provider) and skipped the classic
   registration form. Populates the missing fields (city, birth_year) so
   Match, discovery-by-distance and age filters work day one.

   Trigger rules:
     - user is authenticated
     - user has NOT completed onboarding yet (onboarded_at is null)
     - user is missing BOTH city AND birth_year (heuristic for
       "profile skipped via OAuth")
     - shown ONCE per session: a local flag on the user id avoids re-open
       when they close and come back before finishing.
*/
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { MapPin, Calendar, Sparkles, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SKIP_KEY_PREFIX = "orio_welcome_seen_";

export default function WelcomeCompleteSheet() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [city, setCity] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [saving, setSaving] = useState(false);

  // Only surface for users who signed up via a social provider and never
  // filled in the profile form. We do NOT re-show after they close it in
  // the same browser session even without saving — nagging modals kill
  // conversion. They can complete from /app/profilo anytime.
  const shouldShow = useMemo(() => {
    if (!user) return false;
    if (dismissed) return false;
    if (user.onboarded_at) return false;
    if (user.city || user.birth_year) return false;
    if (user.id) {
      try {
        if (localStorage.getItem(SKIP_KEY_PREFIX + user.id)) return false;
      } catch { /* ignore */ }
    }
    return true;
  }, [user, dismissed]);

  const close = () => {
    setDismissed(true);
    if (user?.id) {
      try { localStorage.setItem(SKIP_KEY_PREFIX + user.id, "1"); } catch {}
    }
  };

  const save = async () => {
    if (saving) return;
    const parsedYear = birthYear ? parseInt(birthYear, 10) : null;
    const cityTrim = city.trim();
    if (!cityTrim && !parsedYear) {
      return toast.error("Aggiungi almeno la città o l'anno di nascita");
    }
    if (parsedYear && (parsedYear < 1920 || parsedYear > new Date().getFullYear() - 12)) {
      return toast.error("Anno di nascita non valido");
    }
    setSaving(true);
    try {
      await api.put("/users/me", {
        city: cityTrim || null,
        birth_year: parsedYear,
      });
      await refresh?.();
      toast.success("Grazie! Adesso ti mostro come funziona ORIO 🐾");
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
    } finally { setSaving(false); }
  };

  if (!shouldShow || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2800] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="welcome-complete-sheet"
    >
      <div
        className="relative w-full sm:max-w-md mx-0 sm:mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white overflow-hidden"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          boxShadow: "0 30px 70px -20px rgba(0,0,0,0.55)",
        }}
      >
        {/* Grabber */}
        <div className="sm:hidden pt-3 pb-1 flex justify-center">
          <span className="h-1 w-10 rounded-full bg-[#E5DECF]" />
        </div>

        {/* Header */}
        <div className="px-5 pt-2 pb-3 flex items-start gap-3">
          <div
            className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 text-white"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
              boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)",
            }}
          >
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C25612]">
              Un ultimo passo
            </div>
            <div className="font-display text-xl leading-tight text-[#1A1A1A] mt-0.5">
              Benvenuto{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
            </div>
            <div className="text-[13px] text-[#6B6B6B] mt-1.5 leading-relaxed">
              Compila 2 dati veloci per attivare Match, mappa e filtri di distanza.
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 space-y-3 pt-1">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A] flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-[#5B9EE0]" /> La tua città
            </label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Milano, Roma, Napoli…"
              data-testid="welcome-city"
              maxLength={80}
              autoFocus
              className="mt-1 w-full h-11 rounded-2xl border border-[#E5DECF] px-3.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A] flex items-center gap-1.5">
              <Calendar className="h-3 w-3 text-[#5B9EE0]" /> Il tuo anno di nascita
            </label>
            <input
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="1990"
              inputMode="numeric"
              data-testid="welcome-year"
              className="mt-1 w-full h-11 rounded-2xl border border-[#E5DECF] px-3.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
            <div className="text-[11px] text-[#9A9A9A] mt-1">
              Ci serve per mostrare la tua età sul profilo e nei match.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pt-4 pb-2 flex gap-2">
          <button
            onClick={close}
            data-testid="welcome-skip"
            className="flex-1 h-11 rounded-full bg-[#F4EFE6] text-[#3A3A3A] text-[13px] font-semibold"
          >
            Più tardi
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="welcome-save"
            className="flex-[1.4] h-11 rounded-full text-white text-[13px] font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
              boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)",
            }}
          >
            {saving ? "Salvo…" : "Continua"} <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
